import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Save, Settings, Trash2, Plus, Mail, User, Phone, ToggleLeft } from "lucide-react";

const SITE_TYPE_META = {
    MANAGED_CLIENT: { label: "Managed Client", cls: "bg-[#EEF2F8] text-[#1E3355] border-[#C7D1E2]" },
    OWN_BUILDING:   { label: "Own Building",   cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    COWORKING:      { label: "Coworking",      cls: "bg-violet-50 text-violet-800 border-violet-200" },
};

export default function SiteSettingsPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [form, setForm] = useState({ address: "", notify_emails: [], client_contact_name: "", client_contact_phone: "", monthly_fee_inr: "" });
    const [toggles, setToggles] = useState({ attendance: false, checklist: false, diary: false });
    const [emailInput, setEmailInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [togglesSaving, setTogglesSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/sites/${siteId}`);
            setSite(data);
            setForm({
                address: data.address || "",
                notify_emails: data.notify_emails || [],
                client_contact_name: data.client_contact_name || "",
                client_contact_phone: data.client_contact_phone || "",
                monthly_fee_inr: data.monthly_fee_inr ?? "",
            });
            setToggles({
                attendance: !!(data.feature_toggles?.attendance),
                checklist:  !!(data.feature_toggles?.checklist),
                diary:      !!(data.feature_toggles?.diary),
            });
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [siteId]);

    function addEmail() {
        const e = emailInput.trim().toLowerCase();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Enter a valid email address"); return; }
        if (form.notify_emails.includes(e)) return;
        setForm({ ...form, notify_emails: [...form.notify_emails, e] });
        setEmailInput("");
    }
    function removeEmail(e) { setForm({ ...form, notify_emails: form.notify_emails.filter((x) => x !== e) }); }

    async function save() {
        setSaving(true);
        try {
            const payload = {
                address: form.address,
                notify_emails: form.notify_emails,
                client_contact_name: form.client_contact_name,
                client_contact_phone: form.client_contact_phone,
            };
            if (siteType === "MANAGED_CLIENT" && form.monthly_fee_inr !== "" && form.monthly_fee_inr !== null) {
                payload.monthly_fee_inr = parseFloat(form.monthly_fee_inr);
            }
            const { data } = await api.patch(`/sites/${siteId}`, payload);
            setSite(data);
            toast.success("Settings saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    async function toggleModule(key, value) {
        setTogglesSaving(true);
        // Optimistic UI
        const previous = toggles;
        const next = { ...toggles, [key]: value };
        setToggles(next);
        try {
            const { data } = await api.patch(`/sites/${siteId}`, { feature_toggles: { [key]: value } });
            setSite(data);
            setToggles({
                attendance: !!(data.feature_toggles?.attendance),
                checklist:  !!(data.feature_toggles?.checklist),
                diary:      !!(data.feature_toggles?.diary),
            });
            toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} ${value ? "enabled" : "disabled"}`);
        } catch (e) {
            setToggles(previous);
            toast.error(apiError(e));
        } finally { setTogglesSaving(false); }
    }

    const siteType = site?.site_type || "MANAGED_CLIENT";
    const typeMeta = SITE_TYPE_META[siteType] || SITE_TYPE_META.MANAGED_CLIENT;
    const supportsToggles = siteType !== "MANAGED_CLIENT";

    if (loading || !site) return <div className="mx-auto max-w-3xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;
    const notes = site.contract_notes || [];

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3"><ChevronLeft className="w-4 h-4" /> Sites</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Site settings</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><Settings className="w-6 h-6" /> {site.name}</h1>
                    <div className="mt-2">
                        <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border uppercase tracking-wider ${typeMeta.cls}`}
                            data-testid="site-type-badge"
                        >
                            {typeMeta.label}
                        </span>
                    </div>
                </div>
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60" data-testid="site-settings-save">
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
                </button>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5 space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={fieldCls} data-testid="site-address" />
                </div>
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Notification & report emails</label>
                        <span className="text-[11px] text-gray-500">Falls back to global HO_NOTIFY_EMAILS if empty</span>
                    </div>
                    <div className="flex gap-2">
                        <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }} placeholder="add address and press Enter" className={fieldCls} data-testid="site-email-input" />
                        <button onClick={addEmail} className="inline-flex items-center gap-1 bg-white border border-gray-200 text-[#1E3355] text-sm font-medium px-3 py-2 rounded-md hover:border-[#1E3355]" data-testid="site-email-add"><Plus className="w-3.5 h-3.5" /> Add</button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="site-emails-list">
                        {form.notify_emails.length === 0 ? <span className="text-xs text-gray-400 italic">No per-site emails set — using global HO_NOTIFY_EMAILS.</span> :
                         form.notify_emails.map((e) => (
                            <span key={e} className="inline-flex items-center gap-1 bg-[#F4F6F9] border border-gray-200 rounded-full px-2.5 py-1 text-xs text-[#1E3355]" data-testid={`site-email-chip-${e}`}>
                                {e}
                                <button onClick={() => removeEmail(e)} className="text-[#E02B2B] hover:text-[#C92525]" data-testid={`site-email-remove-${e}`}><Trash2 className="w-3 h-3" /></button>
                            </span>
                         ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Client contact name</label>
                        <input value={form.client_contact_name} onChange={(e) => setForm({ ...form, client_contact_name: e.target.value })} className={fieldCls} data-testid="site-contact-name" placeholder="e.g. Rahul Sen — Admin Head" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Client contact phone</label>
                        <input value={form.client_contact_phone} onChange={(e) => setForm({ ...form, client_contact_phone: e.target.value })} className={fieldCls} data-testid="site-contact-phone" placeholder="+91-…" />
                    </div>
                </div>
                <p className="text-[11px] text-gray-500 italic">Client contact is surfaced as a read-through hint on tasks that reference the client (SMIFS tasks 27 &amp; 32).</p>
                <div className="pt-4 border-t border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client monthly fee ₹ (lump sum)</label>
                    {siteType === "MANAGED_CLIENT" ? (
                        <>
                            <input
                                type="number" min="0" step="1"
                                value={form.monthly_fee_inr}
                                onChange={(e) => setForm({ ...form, monthly_fee_inr: e.target.value })}
                                className={fieldCls}
                                data-testid="site-monthly-fee"
                                placeholder="e.g. 150000"
                            />
                            {site.monthly_fee_is_placeholder && (
                                <div className="mt-1 text-[11px] text-amber-700" data-testid="fee-placeholder-warning">
                                    Currently a placeholder value — please replace with the real contracted monthly fee.
                                </div>
                            )}
                        </>
                    ) : (
                        <div
                            className="rounded-md border border-gray-200 bg-[#F4F6F9] px-3 py-2 text-xs text-gray-600 italic"
                            data-testid="fee-not-applicable"
                        >
                            Not applicable on {typeMeta.label} sites — margin math uses ₹0 fee (expenses only).
                        </div>
                    )}
                </div>
            </div>

            {supportsToggles && (
                <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5" data-testid="feature-toggles-card">
                    <div className="flex items-center gap-2 mb-1">
                        <ToggleLeft className="w-5 h-5 text-[#1E3355]" />
                        <h2 className="font-display font-bold text-lg text-[#1E3355]">Optional field modules</h2>
                    </div>
                    <p className="text-[12px] text-gray-500 mb-4">
                        Enable only the modules the field team actually runs at this site. Client-facing modules
                        (SLA lookup, billable log, client report portal, service requests) are always disabled on
                        {" "}{typeMeta.label} sites and cannot be turned on.
                    </p>
                    <div className="divide-y divide-gray-100">
                        {[
                            { key: "checklist",  label: "Daily manager checklist", desc: "Runs the daily 60-task checklist, monthly tracker and sign-off flow." },
                            { key: "attendance", label: "Attendance & roster",     desc: "Track shift attendance, absences and roster." },
                            { key: "diary",      label: "Site diary",              desc: "Free-form daily site notes and observations." },
                        ].map((m) => (
                            <label
                                key={m.key}
                                className="flex items-start justify-between gap-4 py-3 cursor-pointer"
                                data-testid={`toggle-row-${m.key}`}
                            >
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-[#1E3355]">{m.label}</div>
                                    <div className="text-[11px] text-gray-500 mt-0.5">{m.desc}</div>
                                </div>
                                <span className="flex items-center gap-2 shrink-0">
                                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${toggles[m.key] ? "text-emerald-700" : "text-gray-400"}`}>
                                        {toggles[m.key] ? "On" : "Off"}
                                    </span>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={toggles[m.key]}
                                        disabled={togglesSaving}
                                        onClick={() => toggleModule(m.key, !toggles[m.key])}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${toggles[m.key] ? "bg-emerald-500" : "bg-gray-300"}`}
                                        data-testid={`toggle-${m.key}`}
                                    >
                                        <span
                                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${toggles[m.key] ? "translate-x-5" : "translate-x-0.5"}`}
                                        />
                                    </button>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {notes.length > 0 && (
                <div className="mt-6">
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Contract notes (from agreement — read-only)</div>
                    <div className="space-y-2">
                        {notes.map((n) => (
                            <div key={n.id} className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900" data-testid={`contract-note-${n.id}`}>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-0.5">{n.category}</div>
                                {n.text}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
